/* assets/variant-gallery.js
   Variant gallery with:
   - Skeleton shimmer while loading
   - Hover zoom on desktop (follows cursor) if Dawn "Zoom on hover" is ON — images only
   - Lightbox with pinch‑to‑zoom (two fingers), click‑to‑zoom, drag‑to‑pan, and wheel zoom — images only
   - Video support: items with type "video" render as <video muted autoplay loop playsinline>
     in the main slot and the desktop grid; thumbnails show the video's poster frame with a
     small play badge. The lightbox plays the video with native controls instead of zooming it.
   - Fallback to Dawn gallery if a variant has no custom images

   Requires:
   - Container:  <div id="VariantGallery-{{ section.id }}" data-variant-gallery data-section-id="{{ section.id }}"></div>
   - JSON map:   <script id="VariantGalleries-{{ section.id }}" type="application/json">{"variantId":[{type,url|videoUrl,thumb,alt,...}, ...]}</script>
   - CSS: make sure .vg-lb-img has touch-action:none (already in the CSS I gave)
*/

(() => {
  function isVideo(item) {
    return item && item.type === 'video';
  }

  function mediaMarkup(item, index, { main } = {}) {
    const idAttr = main ? ' id="vg-main-media"' : '';
    const altText = (item.alt || '').replace(/"/g, '&quot;');
    if (isVideo(item)) {
      return `<video${idAttr} class="vg-zoom-img vg-video" src="${item.videoUrl}" poster="${item.poster || ''}" autoplay muted loop playsinline preload="metadata" aria-label="${altText}"></video>`;
    }
    // Fix: this previously evaluated to "eager" on BOTH branches, which forced every
    // grid/thumbnail image to compete for bandwidth with the hero image on every
    // render. Only the main slot should be eager (and prioritized); everything else
    // should be lazy so the browser fetches the visible hero image first.
    const loadingAttr = main ? 'eager' : 'lazy';
    const fetchPriorityAttr = main ? ' fetchpriority="high"' : '';
    return `<img${idAttr} class="vg-zoom-img" src="${item.url}" ${item.srcset ? `srcset="${item.srcset}"` : ''} alt="${altText}" loading="${loadingAttr}"${fetchPriorityAttr}>`;
  }

  function mainSlotMarkup(item) {
    return `
      <div class="vg-zoom${isVideo(item) ? ' vg-zoom--video' : ''}" data-index="0">
        ${mediaMarkup(item, 0, { main: true })}
      </div>`;
  }

  function initVG(root) {
    if (root.__vgBound) return;
    root.__vgBound = true;

    const sectionId = root.dataset.sectionId || '';
    const jsonEl = document.getElementById(`VariantGalleries-${sectionId}`);
    if (!jsonEl) return;

    let map = {};
    try { map = JSON.parse(jsonEl.textContent || '{}'); } catch (_) { map = {}; }

    const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);
    const productInfo = document.getElementById(`ProductInfo-${sectionId}`);
    const variantInput = productInfo?.querySelector('input[name="id"]');
    if (!variantInput) return;

    const productInfoWrapper = document.getElementById(`MainProduct-${sectionId}`) || document.querySelector(`product-info[data-section="${sectionId}"]`);
    const hoverZoomEnabled = productInfoWrapper?.hasAttribute('data-zoom-on-hover');

    let currentImages = [];
    let renderToken = 0;
    let loaderEl = null;
    let lastHeight = 0;

    // ---------- Skeleton loader ----------
    function skeletonMarkup() {
      return `
        <div class="vg-skeleton-mobile">
          <div class="vg-skel vg-skel-main"></div>
          <div class="vg-skel-thumbs">
            <div class="vg-skel vg-skel-thumb"></div>
            <div class="vg-skel vg-skel-thumb"></div>
            <div class="vg-skel vg-skel-thumb"></div>
            <div class="vg-skel vg-skel-thumb"></div>
          </div>
        </div>
        <div class="vg-skeleton-desktop">
          <div class="vg-skel vg-skel-tile"></div>
          <div class="vg-skel vg-skel-tile"></div>
          <div class="vg-skel vg-skel-tile"></div>
          <div class="vg-skel vg-skel-tile"></div>
        </div>`;
    }
    function ensureLoader() {
      if (!loaderEl) {
        loaderEl = document.createElement('div');
        loaderEl.className = 'vg-loader is-hidden';
        loaderEl.innerHTML = skeletonMarkup();
        root.appendChild(loaderEl);
      }
    }
    function setLoading(isLoading, token) {
      ensureLoader();
      if (isLoading) {
        const h = root.offsetHeight;
        if (h > 0) {
          lastHeight = h;
          root.style.minHeight = h + 'px';
        }
        root.classList.add('vg-is-loading');
        loaderEl.classList.remove('is-hidden');
        loaderEl.innerHTML = skeletonMarkup();
        root.appendChild(loaderEl);
      } else {
        if (token !== undefined && token !== renderToken) return;
        loaderEl.classList.add('is-hidden');
        setTimeout(() => {
          root.classList.remove('vg-is-loading');
          root.style.minHeight = '';
        }, 180);
      }
    }
    function whenMediaReady(el, timeout = 2500) {
      return new Promise((resolve) => {
        if (!el) return resolve();
        if (el.tagName === 'VIDEO') {
          if (el.readyState >= 2) return resolve(); // HAVE_CURRENT_DATA
          const done = () => { el.removeEventListener('loadeddata', done); el.removeEventListener('error', done); resolve(); };
          el.addEventListener('loadeddata', done, { once: true });
          el.addEventListener('error', done, { once: true });
          setTimeout(done, timeout);
          return;
        }
        if (el.complete && el.naturalWidth) return resolve();
        const done = () => { el.removeEventListener('load', done); el.removeEventListener('error', done); resolve(); };
        el.addEventListener('load', done, { once: true });
        el.addEventListener('error', done, { once: true });
        setTimeout(done, timeout);
      });
    }

    // ---------- Fallback ----------
    function showDefault() {
      setLoading(false);
      root.hidden = true;
      if (mediaGallery) mediaGallery.hidden = false;
    }

    // ---------- Hover zoom (desktop, images only) ----------
    function bindHoverZoom() {
      if (!hoverZoomEnabled) return;
      if (!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
      root.querySelectorAll('.vg-zoom:not(.vg-zoom--video)').forEach(wrap => {
        const img = wrap.querySelector('img');
        if (!img) return;
        wrap.addEventListener('mouseenter', () => wrap.classList.add('is-zooming'));
        wrap.addEventListener('mouseleave', () => wrap.classList.remove('is-zooming'));
        wrap.addEventListener('mousemove', (e) => {
          const r = wrap.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * 100;
          const y = ((e.clientY - r.top) / r.height) * 100;
          wrap.style.setProperty('--x', `${x}%`);
          wrap.style.setProperty('--y', `${y}%`);
        });
      });
    }

    // ---------- Lightbox with pinch‑to‑zoom (images) + native video playback ----------
    let lb, stage, lbImg, lbVideo, lbPrev, lbNext, lbClose, lbIndex = 0;
    let scale = 1, tx = 0, ty = 0, baseW = 0, baseH = 0;
    const MAX_SCALE = 2.4;

    // Dragging with one finger/mouse
    let isDragging = false, startX = 0, startY = 0, startTx = 0, startTy = 0;

    // Pinch state
    const activePointers = new Map(); // id -> {x,y}
    let isPinching = false;
    let pinchStartScale = 1;
    let pinchCenterClient = { x: 0, y: 0 };
    let lastMoveTime = 0; // to throttle a bit

    function ensureLightbox() {
      const id = `vg-lightbox-${sectionId}`;
      lb = document.getElementById(id);
      if (lb) { stage = lb.querySelector('.vg-lb-stage'); lbImg = lb.querySelector('.vg-lb-img'); lbVideo = lb.querySelector('.vg-lb-video'); lbPrev = lb.querySelector('.vg-lb-prev'); lbNext = lb.querySelector('.vg-lb-next'); lbClose = lb.querySelector('.vg-lb-close'); return; }

      lb = document.createElement('div');
      lb.id = id;
      lb.className = 'vg-lightbox';
      lb.innerHTML = `
        <div class="vg-lb-stage">
          <img class="vg-lb-img" alt="">
          <video class="vg-lb-video" muted autoplay loop playsinline controls style="display:none"></video>
          <button class="vg-lb-btn vg-lb-prev" aria-label="Previous">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
          <button class="vg-lb-btn vg-lb-next" aria-label="Next">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
          <button class="vg-lb-close" aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
        </div>`;
      document.body.appendChild(lb);

      stage = lb.querySelector('.vg-lb-stage');
      lbImg = lb.querySelector('.vg-lb-img');
      lbVideo = lb.querySelector('.vg-lb-video');
      lbPrev = lb.querySelector('.vg-lb-prev');
      lbNext = lb.querySelector('.vg-lb-next');
      lbClose = lb.querySelector('.vg-lb-close');

      // Close and nav
      lb.addEventListener('click', (e) => { if (e.target === lb) closeLightbox(); });
      lbClose.addEventListener('click', closeLightbox);
      lbPrev.addEventListener('click', () => showIndex(lbIndex - 1));
      lbNext.addEventListener('click', () => showIndex(lbIndex + 1));
      window.addEventListener('keydown', (e) => {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') showIndex(lbIndex - 1);
        if (e.key === 'ArrowRight') showIndex(lbIndex + 1);
      });

      // Pointer events for drag + pinch (images only — handlers are scoped to lbImg)
      lbImg.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUpOrCancel);
      window.addEventListener('pointercancel', onPointerUpOrCancel);

      // Wheel zoom (desktop, images only)
      lbImg.addEventListener('wheel', (e) => {
        if (!lb.classList.contains('is-open')) return;
        e.preventDefault();
        const target = clampScale(scale + (e.deltaY > 0 ? -0.2 : 0.2));
        zoomToScaleAtPoint(target, e.clientX, e.clientY);
      }, { passive: false });
    }

    function clampScale(s) { return Math.max(1, Math.min(MAX_SCALE, s)); }

    function computeBaseSize() {
      lbImg.style.setProperty('--scale', 1);
      lbImg.style.setProperty('--tx', '0px');
      lbImg.style.setProperty('--ty', '0px');
      const r = lbImg.getBoundingClientRect();
      baseW = r.width; baseH = r.height;
    }
    function applyTransform() {
      const sr = stage.getBoundingClientRect();
      const currW = baseW * scale, currH = baseH * scale;
      const maxX = Math.max(0, (currW - sr.width) / 2);
      const maxY = Math.max(0, (currH - sr.height) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
      lbImg.style.setProperty('--scale', scale);
      lbImg.style.setProperty('--tx', `${tx}px`);
      lbImg.style.setProperty('--ty', `${ty}px`);
    }
    function resetZoom() { scale = 1; tx = 0; ty = 0; applyTransform(); }

    function zoomInToPoint(clientX, clientY) {
      computeBaseSize();
      scale = 1.8;
      // center clicked point
      const sr = stage.getBoundingClientRect();
      const rx = (clientX - sr.left) / sr.width - 0.5;
      const ry = (clientY - sr.top) / sr.height - 0.5;
      const currW = baseW * scale, currH = baseH * scale;
      const maxX = Math.max(0, (currW - sr.width) / 2);
      const maxY = Math.max(0, (currH - sr.height) / 2);
      tx = -rx * (currW - sr.width);
      ty = -ry * (currH - sr.height);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
      applyTransform();
    }

    function zoomToScaleAtPoint(nextScale, clientX, clientY) {
      // Keep the point under cursor fixed while scaling
      const sr = stage.getBoundingClientRect();
      const prev = scale;
      const target = clampScale(nextScale);
      const factor = target / prev;
      const cx = clientX - sr.left - sr.width / 2;
      const cy = clientY - sr.top - sr.height / 2;
      tx = cx - factor * (cx - tx);
      ty = cy - factor * (cy - ty);
      scale = target;
      applyTransform();
    }

    // Pointer handlers (drag + pinch) — only ever attached to lbImg, so video is unaffected
    function onPointerDown(e) {
      if (!lb.classList.contains('is-open')) return;
      lbImg.setPointerCapture(e.pointerId);
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (activePointers.size === 2) {
        // Start pinch
        isPinching = true;
        isDragging = false;
        pinchStartScale = scale;
        pinchCenterClient = midpoint([...activePointers.values()]);
        // ensure we have base sizes
        computeBaseSize();
      } else if (activePointers.size === 1 && scale > 1) {
        // Start drag
        isDragging = true;
        startX = e.clientX; startY = e.clientY;
        startTx = tx; startTy = ty;
      }
    }

    function onPointerMove(e) {
      if (!lb.classList.contains('is-open')) return;
      if (!activePointers.has(e.pointerId)) return;

      // Update this pointer
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      const now = Date.now();
      if (now - lastMoveTime < 10) return; // tiny throttle
      lastMoveTime = now;

      if (activePointers.size === 2) {
        // Pinch in progress
        const pts = [...activePointers.values()];
        const cNow = midpoint(pts);
        const dNow = distance(pts[0], pts[1]);

        // initial distance between the first two pointers when pinch started
        // We compute it on first move if not stored:
        if (!onPointerMove._startDist) {
          onPointerMove._startDist = dNow;
          onPointerMove._lastCenter = cNow;
        }

        const ratio = dNow / onPointerMove._startDist;
        const next = clampScale(pinchStartScale * ratio);

        // Use center point for zoom anchoring
        zoomToScaleAtPoint(next, cNow.x, cNow.y);
      } else if (isDragging && scale > 1 && activePointers.size === 1) {
        // Drag pan with single pointer
        const p = activePointers.get(e.pointerId);
        const dx = p.x - startX;
        const dy = p.y - startY;
        tx = startTx + dx; ty = startTy + dy;
        applyTransform();
      }
    }

    function onPointerUpOrCancel(e) {
      if (activePointers.has(e.pointerId)) activePointers.delete(e.pointerId);
      try { lbImg.releasePointerCapture(e.pointerId); } catch(_) {}

      if (activePointers.size < 2) {
        isPinching = false;
        onPointerMove._startDist = null;
        onPointerMove._lastCenter = null;
      }
      if (activePointers.size === 0) {
        isDragging = false;
      }
    }

    function midpoint([a, b]) {
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
    function distance(a, b) {
      const dx = a.x - b.x, dy = a.y - b.y;
      return Math.hypot(dx, dy);
    }

    function openLightbox(startIndex) {
      ensureLightbox();
      lb.classList.add('is-open');
      resetZoom();
      showIndex(startIndex);
    }
    function closeLightbox() {
      lb.classList.remove('is-open');
      if (lbVideo) lbVideo.pause();
    }
    function showIndex(i) {
      if (!currentImages.length) return;
      if (i < 0) i = currentImages.length - 1;
      if (i >= currentImages.length) i = 0;
      lbIndex = i;
      const item = currentImages[i];

      if (isVideo(item)) {
        lbImg.style.display = 'none';
        lbVideo.style.display = '';
        lbVideo.pause();
        lbVideo.src = item.videoUrl;
        if (item.poster) lbVideo.poster = item.poster;
        lbVideo.load();
        const p = lbVideo.play();
        if (p && p.catch) p.catch(() => {}); // ignore autoplay rejection; muted so should be allowed
      } else {
        if (lbVideo) { lbVideo.pause(); lbVideo.removeAttribute('src'); lbVideo.style.display = 'none'; }
        lbImg.style.display = '';
        lbImg.onload = () => { resetZoom(); computeBaseSize(); applyTransform(); };
        lbImg.src = item.url;
        lbImg.srcset = item.srcset || '';
        lbImg.alt = item.alt || '';
      }
    }
    function bindLightboxClicks() {
      root.addEventListener('click', (e) => {
        const wrap = e.target.closest('.vg-zoom');
        if (!wrap || !root.contains(wrap)) return;
        const idx = parseInt(wrap.dataset.index || '0', 10);
        openLightbox(idx);
      });
    }

    // ---------- Render ----------
    async function render(images) {
      if (!images?.length) return showDefault();
      currentImages = images;

      const myToken = ++renderToken;
      setLoading(true, myToken);

      const desktopGrid = images.map((img, i) => `
        <div class="vg-grid-item">
          <div class="vg-zoom${isVideo(img) ? ' vg-zoom--video' : ''}" data-index="${i}">
            ${mediaMarkup(img, i)}
          </div>
        </div>
      `).join('');

      const thumbs = images.map((img, i) => `
        <button type="button" class="vg-thumb ${i === 0 ? 'is-active' : ''}" data-index="${i}" aria-label="Show ${isVideo(img) ? 'video' : 'image'} ${i + 1}">
          <img src="${img.thumb || img.url}" alt="${img.alt || ''}" loading="lazy">
          ${isVideo(img) ? '<span class="vg-thumb-play" aria-hidden="true"></span>' : ''}
        </button>
      `).join('');

      const img0 = images[0];
      const mobileMain = `
        <div class="vg-main">${mainSlotMarkup(img0)}</div>`;

      root.innerHTML = `
        <div class="vg-desktop"><div class="vg-grid">${desktopGrid}</div></div>
        <div class="vg-mobile">
          ${mobileMain}
          <div class="vg-thumbs">${thumbs}</div>
        </div>
      `;
      ensureLoader(); root.appendChild(loaderEl);
      root.hidden = false;
      if (mediaGallery) mediaGallery.hidden = true;

      const mainMedia = root.querySelector('#vg-main-media');
      await whenMediaReady(mainMedia, 3000);
      setLoading(false, myToken);

      bindHoverZoom();
      bindLightboxClicks();
    }

    function update() {
      const vid = variantInput.value;
      const imgs = map[vid];
      if (imgs && imgs.length) render(imgs); else showDefault();
    }

    // Thumbnail swap (with skeleton)
    root.addEventListener('click', async (e) => {
      const btn = e.target.closest('.vg-thumb');
      if (!btn) return;
      e.preventDefault();

      const i = +btn.dataset.index;
      const imgs = map[(productInfo.querySelector('input[name="id"]') || {}).value];
      if (!imgs || !imgs[i]) return;

      const myToken = ++renderToken;
      setLoading(true, myToken);

      root.querySelectorAll('.vg-thumb').forEach(el => el.classList.remove('is-active'));
      btn.classList.add('is-active');

      const mainSlot = root.querySelector('.vg-main');
      if (mainSlot) {
        // Rebuilt (not just src-swapped) because the media tag itself can change
        // between <img> and <video> depending on which item was selected.
        mainSlot.innerHTML = mainSlotMarkup(imgs[i]);
        const mainMedia = mainSlot.querySelector('#vg-main-media');
        await whenMediaReady(mainMedia, 2500);
        bindHoverZoom();
      }

      setLoading(false, myToken);
    });

    // React to variant change
    variantInput.addEventListener('change', update);
    new MutationObserver(update).observe(variantInput, { attributes: true, attributeFilter: ['value'] });

    update();
  }

  function initAll() {
    document.querySelectorAll('[data-variant-gallery]').forEach(initVG);
  }
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();